import { useMemo } from "react";

import { groupBy, orderBy } from "lodash";

import { ICheck, TaskType } from "__graphql__/types";
import { CheckListItem } from "flows/Inventory/shared/components/ChecksList";
import { priorityToTag } from "flows/Inventory/shared/models/check/serializer";
import { PriorityTag } from "flows/Inventory/shared/models/check/types";
import { DropDownOption } from "ui/DropDown/DropDownList";
import { getShelfDetail } from "utils/item";

export const SHELF_RANGES = {
  ALL: "all",
  RANGE_001_028: "001-028",
  RANGE_029_056: "029-056",
  RANGE_057_084: "057-084",
  RANGE_085_112: "085-112",
  RANGE_OVER_112: ">112",
} as const;

export const shelfOptions: DropDownOption[] = [
  { key: SHELF_RANGES.ALL, label: "All Shelves" },
  { key: SHELF_RANGES.RANGE_001_028, label: "001 - 028" },
  { key: SHELF_RANGES.RANGE_029_056, label: "029 - 056" },
  { key: SHELF_RANGES.RANGE_057_084, label: "057 - 084" },
  { key: SHELF_RANGES.RANGE_085_112, label: "085 - 112" },
  { key: SHELF_RANGES.RANGE_OVER_112, label: "> 112" },
];

export const useFilteredChecks = (
  rawChecks: ICheck[],
  selectedShelf: string | null,
  shelfSortOrder: "asc" | "desc",
  type = TaskType.eoy_inventory_check,
): CheckListItem[] => {
  return useMemo(() => {
    let filtered = rawChecks;

    if (selectedShelf && selectedShelf !== SHELF_RANGES.ALL) {
      filtered = filtered.filter((check: any) => {
        const { shelfNumber } = getShelfDetail(check.shelfNumber);
        const shelf = Number(shelfNumber);

        switch (selectedShelf) {
          case SHELF_RANGES.RANGE_001_028:
            return shelf >= 1 && shelf <= 28;
          case SHELF_RANGES.RANGE_029_056:
            return shelf >= 29 && shelf <= 56;
          case SHELF_RANGES.RANGE_057_084:
            return shelf >= 57 && shelf <= 84;
          case SHELF_RANGES.RANGE_085_112:
            return shelf >= 85 && shelf <= 112;
          case SHELF_RANGES.RANGE_OVER_112:
            return shelf > 112;
          default:
            return true;
        }
      });
    }

    const grouped = Object.entries(groupBy(filtered, "shelfNumber")).map(
      ([shelfNumber, checks]) => ({
        shelfNumber,
        type,
        count: checks.length,
        priority: checks[0].priority,
        id: checks[0].id,
      }),
    );

    return orderBy(grouped, ["shelfNumber"], [shelfSortOrder]);
  }, [rawChecks, selectedShelf, shelfSortOrder, type]);
};

export const useGroupedByPriorityTagChecks = (checks: CheckListItem[]) => {
  return checks.reduce(
    (
      checksAndCountByPriorityTag: Record<PriorityTag, { count: number; checks: CheckListItem[] }>,
      check,
    ) => {
      // First, get the priority tag of the current check
      const priorityTag = priorityToTag[check.priority];
      const checksAndCount = checksAndCountByPriorityTag[priorityTag];
      if (!checksAndCount) {
        return checksAndCountByPriorityTag;
      }
      // Update the checks array by adding the current check
      const updatedChecks = [...checksAndCount.checks, check];

      // Update the count by summing up the counts of the checks in the updated checks array
      const updatedCount = updatedChecks.reduce(
        (acc, updatedCheck) => acc + (updatedCheck.count || 0),
        0,
      );

      // Finally, return the updated priorities map with the count and updated checks
      return {
        ...checksAndCountByPriorityTag,
        [priorityTag]: {
          count: updatedCount,
          checks: updatedChecks,
        },
      };
    },
    {
      urgent: { count: 0, checks: [] },
      top: { count: 0, checks: [] },
      medium: { count: 0, checks: [] },
      low: { count: 0, checks: [] },
    },
  );
};
